import { apiSlice } from './apiSlice';
import type {
  Crop,
  CropPayload,
  CropQueryParams,
  CropTaxonomyTree,
  CropUpdatePayload,
  CropAttribute,
  CropAttributeSummary,
  CropAttributeQueryParams,
  AvailableAttributesQueryParams,
  CropAttributeCreatePayload,
  CropAttributeUpdatePayload,
  CropAttributeBinding,
  TaxonomyAttributeItem,
  TaxonomyScope,
} from '../models/Crop';
import {
  adaptCrop,
  adaptCropAttribute,
  adaptCropAttributeBinding,
  adaptCropAttributeSummary,
  adaptTaxonomyAttributeItem,
  adaptCropTaxonomyTree,
} from '../models/Crop';

export const cropsApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getCrops: build.query<Crop[], CropQueryParams>({
      query: (params) => ({
        url: '/crops',
        params,
      }),
      transformResponse: (response: any) => {
        const items = response?.crops ?? response ?? [];
        return Array.isArray(items) ? items.map((item: any) => adaptCrop(item)) : [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((crop) => ({ type: 'Crop' as const, id: String(crop.id) })),
              { type: 'Crop' as const, id: 'LIST' },
            ]
          : [{ type: 'Crop' as const, id: 'LIST' }],
    }),
    getSaveCrops: build.query<
      Crop[],
      { saveId: string; search?: string; speciesId?: number | string; cultivarId?: number | string; limit?: number; offset?: number }
    >({
      query: ({ saveId, ...params }) => ({
        url: `/saves/${saveId}/crops`,
        params,
      }),
      transformResponse: (response: any) => {
        const items = response?.crops ?? response ?? [];
        return Array.isArray(items) ? items.map((item: any) => adaptCrop(item)) : [];
      },
      providesTags: (result, _error, { saveId }) =>
        result
          ? [
              ...result.map((crop) => ({ type: 'Crop' as const, id: String(crop.id) })),
              { type: 'Crop' as const, id: `SAVE-${saveId}` },
            ]
          : [{ type: 'Crop' as const, id: `SAVE-${saveId}` }],
    }),
    createSaveCrop: build.mutation<Crop, Omit<CropPayload, 'farmId'> & { saveId: string; farmId?: string }>({
      query: ({ saveId, farmId: _farmId, ...body }) => ({
        url: `/saves/${saveId}/crops`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => adaptCrop(response?.crop ?? response),
      invalidatesTags: (_result, _error, { saveId }) => [
        { type: 'Crop', id: `SAVE-${saveId}` },
        { type: 'Crop', id: 'LIST' },
      ],
    }),
    getSaveCropTaxonomyTree: build.query<CropTaxonomyTree, string>({
      query: (saveId) => ({ url: `/saves/${saveId}/crops/taxonomy/tree` }),
      transformResponse: (response: any) => adaptCropTaxonomyTree(response ?? {}),
    }),
    getCropById: build.query<Crop, string | number>({
      query: (id) => ({ url: `/crops/${id}` }),
      transformResponse: (response: any) => adaptCrop(response),
      providesTags: (result, error, id) => [{ type: 'Crop' as const, id: String(id) }],
    }),
    createCrop: build.mutation<Crop, CropPayload>({
      query: (body) => ({ url: '/crops', method: 'POST', body }),
      transformResponse: (response: any) => adaptCrop(response?.crop ?? response),
      invalidatesTags: [{ type: 'Crop', id: 'LIST' }],
    }),
    updateCrop: build.mutation<Crop, { id: string | number; payload: CropUpdatePayload }>({
      query: ({ id, payload }) => ({ url: `/crops/${id}`, method: 'PATCH', body: payload }),
      transformResponse: (response: any) => adaptCrop(response?.crop ?? response),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Crop' as const, id: String(id) },
        { type: 'Crop' as const, id: 'LIST' },
      ],
    }),
    deleteCrop: build.mutation<void, string | number>({
      query: (id) => ({ url: `/crops/${id}`, method: 'DELETE' }),
      invalidatesTags: (result, error, id) => [
        { type: 'Crop' as const, id: String(id) },
        { type: 'Crop' as const, id: 'LIST' },
      ],
    }),
    getCropAttributes: build.query<CropAttributeSummary[], CropAttributeQueryParams>({
      query: ({ saveId, ...rest }) => {
        const queryParams: Record<string, string> = {};
        if (rest?.search) queryParams.search = rest.search;
        if (rest?.scopeType) queryParams.scopeType = String(rest.scopeType);
        return {
          url: `/saves/${saveId}/attributes`,
          ...(Object.keys(queryParams).length ? { params: queryParams } : {}),
        };
      },
      transformResponse: (response: any) => {
        const items = response?.attributes ?? response ?? [];
        return Array.isArray(items) ? items.map((item: any) => adaptCropAttributeSummary(item)) : [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((attribute) => ({ type: 'CropAttribute' as const, id: String(attribute.id) })),
              { type: 'CropAttribute' as const, id: 'LIST' },
            ]
          : [{ type: 'CropAttribute' as const, id: 'LIST' }],
    }),
    getAvailableAttributes: build.query<CropAttributeSummary[], AvailableAttributesQueryParams>({
      query: ({ scope, scopeId, saveId }) => ({
        url: `/saves/${saveId}/attributes/available`,
        params: {
          scope: String(scope),
          scopeId: String(scopeId),
        },
      }),
      transformResponse: (response: any) => {
        const items = response?.attributes ?? response ?? [];
        return Array.isArray(items) ? items.map((item: any) => adaptCropAttributeSummary(item)) : [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((attribute) => ({ type: 'CropAttribute' as const, id: String(attribute.id) })),
              { type: 'CropAttribute' as const, id: 'AVAILABLE' },
            ]
          : [{ type: 'CropAttribute' as const, id: 'AVAILABLE' }],
    }),
    getCropAttributeById: build.query<CropAttribute, { id: string | number; saveId: string }>({
      query: ({ id, saveId }) => ({ url: `/saves/${saveId}/attributes/${id}` }),
      transformResponse: (response: any) => adaptCropAttribute(response?.attribute ?? response),
      providesTags: (_r, _e, { id }) => [{ type: 'CropAttribute' as const, id: String(id) }],
    }),
    createCropAttribute: build.mutation<
      CropAttribute,
      CropAttributeCreatePayload & { saveId: string }
    >({
      query: ({ saveId, ...body }) => ({
        url: `/saves/${saveId}/attributes`,
        method: 'POST',
        body,
      }),
      transformResponse: (response: any) => adaptCropAttribute(response?.attribute ?? response),
      invalidatesTags: [
        { type: 'CropAttribute', id: 'LIST' },
        { type: 'CropAttribute', id: 'AVAILABLE' },
      ],
    }),
    updateCropAttribute: build.mutation<
      CropAttribute,
      { id: string | number; saveId: string; payload: CropAttributeUpdatePayload }
    >({
      query: ({ id, saveId, payload }) => ({
        url: `/saves/${saveId}/attributes/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: any) => adaptCropAttribute(response?.attribute ?? response),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'CropAttribute' as const, id: String(id) },
        { type: 'CropAttribute' as const, id: 'LIST' },
        { type: 'CropAttribute' as const, id: 'AVAILABLE' },
      ],
    }),
    deleteCropAttribute: build.mutation<void, { id: string | number; saveId: string }>({
      query: ({ id, saveId }) => ({
        url: `/saves/${saveId}/attributes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'CropAttribute' as const, id: String(id) },
        { type: 'CropAttribute' as const, id: 'LIST' },
        { type: 'CropAttribute' as const, id: 'AVAILABLE' },
      ],
    }),
    createAttributeBinding: build.mutation<
      CropAttributeBinding,
      { attributeId: string | number; scope: TaxonomyScope; scopeId: string | number; saveId: string }
    >({
      query: ({ attributeId, scope, scopeId, saveId }) => ({
        url: `/saves/${saveId}/attributes/${attributeId}/bindings`,
        method: 'POST',
        body: { scope, scopeId },
      }),
      transformResponse: (response: any) => adaptCropAttributeBinding(response?.binding ?? response),
      invalidatesTags: (_r, _e, { attributeId }) => [
        { type: 'CropAttribute' as const, id: String(attributeId) },
        { type: 'CropAttribute' as const, id: 'LIST' },
      ],
    }),
    getTaxonomyAttributes: build.query<
      TaxonomyAttributeItem[],
      { scope: TaxonomyScope; scopeId: string | number; saveId: string }
    >({
      query: ({ scope, scopeId, saveId }) => ({
        url: `/saves/${saveId}/taxonomy/${scope}/${scopeId}/attributes`,
      }),
      transformResponse: (response: any) => {
        const items = response?.attributes ?? response ?? [];
        return Array.isArray(items) ? items.map((item: any) => adaptTaxonomyAttributeItem(item)) : [];
      },
    }),
    setTaxonomyAttributeValue: build.mutation<
      CropAttributeBinding,
      {
        scope: TaxonomyScope;
        scopeId: string | number;
        attributeId: string | number;
        saveId: string;
        choiceId?: string | number | null;
        customValue?: string | null;
      }
    >({
      query: ({ scope, scopeId, attributeId, saveId, choiceId, customValue }) => {
        const body: Record<string, any> = {};
        if (choiceId !== undefined) {
          if (choiceId === null || choiceId === '') {
            body.choiceId = null;
          } else {
            const parsedChoice = Number(choiceId);
            body.choiceId = Number.isNaN(parsedChoice) ? choiceId : parsedChoice;
          }
        }
        if (customValue !== undefined) {
          body.customValue = customValue;
        }

        const hasBody = Object.keys(body).length > 0;

        return {
          url: `/saves/${saveId}/taxonomy/${scope}/${scopeId}/attributes/${attributeId}`,
          method: 'PUT',
          ...(hasBody ? { body } : {}),
        };
      },
      transformResponse: (response: any) => adaptCropAttributeBinding(response?.binding ?? response),
      invalidatesTags: (result, error, { scope, scopeId, attributeId }) => [
        { type: 'CropAttribute' as const, id: String(attributeId) },
        { type: 'CropAttribute' as const, id: 'LIST' },
        ...(scope === 'crop'
          ? [{ type: 'Crop' as const, id: String(scopeId) }]
          : []),
      ],
    }),
    getCropTaxonomyTree: build.query<CropTaxonomyTree, void>({
      query: () => ({ url: '/crops/taxonomy/tree' }),
      transformResponse: (response: any) => adaptCropTaxonomyTree(response ?? {}),
    }),
    /**
     * Удалить привязку атрибута к культуре (crop attribute binding)
     */
    deleteCropAttributeBinding: build.mutation<
      void,
      { cropId: string | number; bindingId: number }
    >({
      query: ({ cropId, bindingId }) => ({
        url: `/crops/${cropId}/attributes/${bindingId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { cropId }) => [
        { type: 'Crop' as const, id: String(cropId) },
        { type: 'CropAttribute' as const, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetCropsQuery,
  useGetSaveCropsQuery,
  useCreateSaveCropMutation,
  useGetSaveCropTaxonomyTreeQuery,
  useGetCropByIdQuery,
  useCreateCropMutation,
  useUpdateCropMutation,
  useDeleteCropMutation,
  useGetCropAttributesQuery,
  useGetAvailableAttributesQuery,
  useGetCropAttributeByIdQuery,
  useCreateCropAttributeMutation,
  useUpdateCropAttributeMutation,
  useDeleteCropAttributeMutation,
  useCreateAttributeBindingMutation,
  useGetTaxonomyAttributesQuery,
  useSetTaxonomyAttributeValueMutation,
  useGetCropTaxonomyTreeQuery,
  useDeleteCropAttributeBindingMutation,
} = cropsApi;
